/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
type Range = { min: number; max: number };

/**
 * Gets the new range given the initial, global, and range ratio to apply. This applies a linear scale.
 * @param rangeRatio The range ratio to scale by.
 * @param initialRange The initial range.
 * @param globalRange The global range.
 * @returns The new linearly scaled range.
 */
const getNewRange = (rangeRatio: number, initialRange: Range, globalRange: Range) => {
  const dRangeMin = initialRange.max - globalRange.max;
  const dRangeMax = initialRange.min - globalRange.min;
  const dRange = rangeRatio * (initialRange.max - initialRange.min);
  const dRangeClamped = Math.max(dRangeMin, Math.min(dRange, dRangeMax));
  return {
    // Ensure positive min;
    // For vertical scroll for example,
    // it's possible for globalRange to be smaller than initialRange
    // due to the component being taller than the height the data occupies
    // e.g. tall Gantt with 1 row
    min: Math.max(0, initialRange.min - dRangeClamped),
    max: initialRange.max - dRangeClamped
  };
};

/**
 * Performs a pan, by invoking the given onRangeChange callback with the new range.
 * @param rangeRatio The range ratio to scale by.
 * @param initialRange The initial range.
 * @param prevRange The previous range.
 * @param globalRange The global range.
 * @param onRangeChange The callback to invoke with the new range.
 * @returns The new range and whether the range actually changed.
 */
const pan = (
  rangeRatio: number,
  initialRange: Range,
  prevRange: Range,
  globalRange: Range,
  onRangeChange: (detail: Range) => void
) => {
  const newRange = getNewRange(rangeRatio, initialRange, globalRange);
  const { min: prevMin, max: prevMax } = prevRange;
  const isRangeChanged = newRange.min !== prevMin || newRange.max !== prevMax;
  isRangeChanged && onRangeChange(newRange);
  return { newRange, isRangeChanged };
};

export { type Range, pan };
